import {
  ConflictException,
  Currency,
  getPricingById,
  getPricingSpecsByExactConditions,
  NotFoundException,
  patchAddPricingInsurancesByPricingSpecId,
  patchAddPricingSpecToPricing,
  patchAddTagToPricing,
  patchRemovePricingSpecInLenderContextFromPricingSpecByLenderId,
  patchSetNameAndDescriptionForPricingById,
  postCreatePricingSpec,
  PricingSpecRequest,
  TermInterval,
  TermType,
} from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { PricingSpecSearchEnabledException } from '@/components/modules/PricingEditor/exceptions/PricingSpecSearchEnabledException';
import { PricingSpecRequestMap } from '@/components/modules/PricingEditor/maps/pricingSpecRequest.map';
import { LenderEarningFee } from '@/components/modules/PricingEditor/types/LenderEarningFee';
import { PricingSpecFormValues } from '@/components/modules/PricingEditor/types/PricingSpecFormValues';
import { PricingValueObject } from '@/components/modules/PricingEditor/types/PricingValueObject';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

export const usePricingEditor = (pricingId: string) => {
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();
  const { request } = useRequest();
  const { data: pricing, revalidate, resetCache, loading: pricingLoading } = useCache(getPricingById, { pricingId });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pricingId) throw new Error('No pricing ID found in URL');
  }, []);

  const resetPricing = () => {
    resetCache();
  };

  const changePricingName = async (name: string, description: string) => {
    try {
      request(patchSetNameAndDescriptionForPricingById, {
        pricingId,
        name,
        description,
      });
      await revalidate();
    } catch (err) {
      setSnackbarNotification({
        msg: 'REQUEST_ERROR',
        level: 'error',
        error: err,
      });
      throw err;
    }
  };

  const addTagToPricing = async (tag: string) => {
    try {
      await request(patchAddTagToPricing, { pricingId, tag });
      await revalidate();
    } catch (err) {
      setSnackbarNotification({
        msg: 'REQUEST_ERROR',
        level: 'error',
        error: err,
      });
      throw err;
    }
  };

  const addPricingSpecToPricing = async (pricingSpecId: string) => {
    try {
      await request(patchAddPricingSpecToPricing, { pricingId, pricingSpecId });
      await revalidate();
    } catch (err) {
      setSnackbarNotification({
        msg: 'REQUEST_ERROR',
        level: 'error',
        error: err,
      });
      throw err;
    }
  };

  const createOrGetPricingSpecAndAddToPricing = async (
    data: PricingSpecFormValues,
    pricingSpecSearchEnabled: boolean = true,
  ): Promise<void> => {
    if (!pricing) return;
    setLoading(true);
    try {
      const pricingSpecRequest = PricingSpecRequestMap.fromFormValues(
        uuidv4(),
        pricing.product,
        pricing.program,
        pricing.paymentMethodTypes[0],
        data,
      );
      const newPricingSpecRequest = await getOrCreatePricingSpec(pricingSpecRequest, pricingSpecSearchEnabled);

      await request(patchAddPricingSpecToPricing, {
        pricingId,
        pricingSpecId: newPricingSpecRequest.request.id,
      });

      if (newPricingSpecRequest.availableInsurancesIds && newPricingSpecRequest.availableInsurancesIds.length > 0) {
        await request(patchAddPricingInsurancesByPricingSpecId, {
          id: newPricingSpecRequest.request.id,
          insuranceIds: newPricingSpecRequest.availableInsurancesIds,
        });
      }
      revalidate();
    } catch (err) {
      setSnackbarNotification({
        msg: 'REQUEST_ERROR',
        level: 'error',
        error: err,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getOrCreatePricingSpec = async (
    pricingSpecRequest: PricingSpecRequest,
    pricingSpecSearchEnabled: boolean = true,
  ): Promise<{ request: PricingSpecRequest; availableInsurancesIds: string[] | null }> => {
    setLoading(true);
    try {
      if (pricingSpecSearchEnabled === false) {
        throw new PricingSpecSearchEnabledException();
      }
      if (!pricingSpecRequest.rangePeriodMax) {
        throw new Error('No Range Period Max found');
      }
      if (!pricingSpecRequest.rangePeriodMin) {
        throw new Error('No Range Period Min found');
      }

      const pricingSpecs = await getPricingSpecsByExactConditions({
        rangePrincipalMin: pricingSpecRequest.rangePrincipalMin,
        rangePrincipalMax: pricingSpecRequest.rangePrincipalMax,
        rangePeriodMin: pricingSpecRequest.rangePeriodMin,
        rangePeriodMax: pricingSpecRequest.rangePeriodMax,
        program: pricingSpecRequest.program,
        product: pricingSpecRequest.product,
        currency: Currency.EUR,
        termInterval: TermInterval.P1M,
        termType: TermType.TYPE_PERIOD,
        ...(pricingSpecRequest.setupFeePaymentType !== undefined && {
          feePaymentType: pricingSpecRequest.setupFeePaymentType,
        }),
        ...(pricingSpecRequest.setupFeeType !== undefined && { feeType: pricingSpecRequest.setupFeeType }),
        ...(pricingSpecRequest.setupFeeValue !== undefined && {
          feeAmount: parseFloat(pricingSpecRequest.setupFeeValue.toFixed(4)),
        }),
        ...(pricingSpecRequest.interest !== undefined &&
          pricingSpecRequest.interest !== null && {
            annualInterest: parseFloat(pricingSpecRequest.interest.toFixed(4)),
          }),
        ...(pricingSpecRequest.gracePeriod !== undefined && { gracePeriod: pricingSpecRequest.gracePeriod }),
        ...(pricingSpecRequest.merchantDiscountPercentage !== undefined && {
          merchantDiscountPercentage: parseFloat(pricingSpecRequest.merchantDiscountPercentage.toFixed(4)),
        }),
      });
      if (pricingSpecs.length > 1) {
        throw new Error('PricingSpecs are larger than 1d');
      }
      if (pricingSpecs.length === 0) {
        throw new Error('No PricingSpecs found');
      }
      console.log('✓ PricingSpec found...');
      return {
        request: PricingSpecRequestMap.fromPricingSpec(pricingSpecs[0]),
        availableInsurancesIds: pricingSpecs[0].availableInsurances
          ? pricingSpecs[0].availableInsurances.map((i) => i.id)
          : null,
      };
    } catch (err) {
      if (
        err instanceof NotFoundException ||
        err instanceof ConflictException ||
        err instanceof PricingSpecSearchEnabledException
      ) {
        console.log('✗ PricingSpec not found, creating new...');
        await request(postCreatePricingSpec, pricingSpecRequest);

        return {
          request: pricingSpecRequest,
          availableInsurancesIds:
            pricing?.ranges[0]?.availableInsurances && pricing?.ranges[0]?.availableInsurances.length > 0
              ? pricing.ranges[0].availableInsurances.map((insurance) => insurance.id)
              : null,
        };
      } else {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  const removePricingInLenderContexts = async (lenderEarningFees: LenderEarningFee[]): Promise<void> => {
    if (!pricing) {
      throw new Error('No Pricing found');
    }
    const pricingVO = new PricingValueObject(pricing);
    try {
      for (const lenderEarningFee of lenderEarningFees) {
        const ranges = pricingVO.rangesByLenderEarningFees(lenderEarningFee);
        for (const range of ranges) {
          await request(patchRemovePricingSpecInLenderContextFromPricingSpecByLenderId, {
            pricingSpecId: range.id,
            lenderId: lenderEarningFee.lenderId,
          });
        }
      }
      setSnackbarNotification({
        msg: 'REQUEST_PATCH_REMOVE_PRICING_SPEC_IN_LENDER_CONTEXT_FROM_PRICING_SPEC_BY_LENDER_ID_FULFILLED',
        level: 'success',
      });
    } catch (err) {
      setSnackbarNotification({
        msg: 'REQUEST_ERROR',
        level: 'error',
        error: err,
      });
      throw err;
    }
    revalidate();
  };

  return {
    pricing,
    getPricing: revalidate,
    resetPricing,
    addTagToPricing,
    changePricingName,
    getOrCreatePricingSpec,
    addPricingSpecToPricing,
    createOrGetPricingSpecAndAddToPricing,
    removePricingInLenderContexts,
    loading: pricingLoading || loading,
  };
};
